package twist.mena.sports.admin.screens.notifications

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminNotificationSettingsApi
import twist.mena.sports.admin.components.FormField
import twist.mena.sports.admin.components.Modal
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminNotificationDisplayItem
import twist.mena.sports.shared.dto.admin.AdminNotificationEventType
import twist.mena.sports.shared.dto.admin.AdminNotificationGroup
import twist.mena.sports.shared.dto.admin.AdminTemplatePreviewRequest
import twist.mena.sports.shared.dto.admin.AdminTemplatePreviewResponse

@Composable
fun GroupDialog(group: AdminNotificationGroup, api: AdminNotificationSettingsApi, onSaved: () -> Unit, onClose: () -> Unit) {
    var nameEn by remember(group) { mutableStateOf(group.nameEn) }
    var nameAr by remember(group) { mutableStateOf(group.nameAr) }
    var descriptionEn by remember(group) { mutableStateOf(group.descriptionEn.orEmpty()) }
    var descriptionAr by remember(group) { mutableStateOf(group.descriptionAr.orEmpty()) }
    var sortOrder by remember(group) { mutableStateOf(group.sortOrder) }
    var defaultEnabled by remember(group) { mutableStateOf(group.defaultEnabled) }
    var active by remember(group) { mutableStateOf(group.active) }

    Modal(title = "Edit toggle: ${group.code}", isOpen = true, onClose = onClose, maxWidth = 640.px) {
        TextField("Name (English)", nameEn) { nameEn = it }
        TextField("Name (Arabic)", nameAr, rtl = true) { nameAr = it }
        TextField("Description (English)", descriptionEn, multiline = true) { descriptionEn = it }
        TextField("Description (Arabic)", descriptionAr, multiline = true, rtl = true) { descriptionAr = it }
        NumberField("Order", sortOrder) { sortOrder = it }
        CheckField("On by default for users who have not chosen", defaultEnabled) { defaultEnabled = it }
        CheckField("Active (shown to users and sent)", active) { active = it }
        SaveBar(onClose) {
            api.updateGroup(
                group.copy(
                    nameEn = nameEn, nameAr = nameAr, descriptionEn = descriptionEn, descriptionAr = descriptionAr,
                    sortOrder = sortOrder, defaultEnabled = defaultEnabled, active = active
                )
            )
            onSaved()
        }
    }
}

@Composable
fun DisplayItemDialog(
    item: AdminNotificationDisplayItem?,
    groups: List<AdminNotificationGroup>,
    api: AdminNotificationSettingsApi,
    onSaved: () -> Unit,
    onClose: () -> Unit
) {
    var code by remember(item) { mutableStateOf(item?.code.orEmpty()) }
    var groupCode by remember(item) { mutableStateOf(item?.groupCode ?: groups.firstOrNull()?.code.orEmpty()) }
    var nameEn by remember(item) { mutableStateOf(item?.nameEn.orEmpty()) }
    var nameAr by remember(item) { mutableStateOf(item?.nameAr.orEmpty()) }
    var descriptionEn by remember(item) { mutableStateOf(item?.descriptionEn.orEmpty()) }
    var descriptionAr by remember(item) { mutableStateOf(item?.descriptionAr.orEmpty()) }
    var sortOrder by remember(item) { mutableStateOf(item?.sortOrder ?: 0) }
    var active by remember(item) { mutableStateOf(item?.active ?: true) }

    Modal(title = if (item == null) "Add display item" else "Edit display item: ${item.code}", isOpen = true, onClose = onClose, maxWidth = 640.px) {
        if (item == null) TextField("Code (lowercase letters, digits, underscore)", code) { code = it.trim() }
        SelectField("Toggle", groupCode, groups.map { it.code to "${it.nameEn} (${it.code})" }) { groupCode = it }
        TextField("Name (English)", nameEn) { nameEn = it }
        TextField("Name (Arabic)", nameAr, rtl = true) { nameAr = it }
        TextField("Description (English)", descriptionEn, multiline = true) { descriptionEn = it }
        TextField("Description (Arabic)", descriptionAr, multiline = true, rtl = true) { descriptionAr = it }
        NumberField("Order", sortOrder) { sortOrder = it }
        CheckField("Active (shown to users)", active) { active = it }
        SaveBar(onClose) {
            api.saveDisplayItem(
                AdminNotificationDisplayItem(
                    code = code, groupCode = groupCode, nameAr = nameAr, nameEn = nameEn,
                    descriptionAr = descriptionAr, descriptionEn = descriptionEn, sortOrder = sortOrder, active = active
                )
            )
            onSaved()
        }
    }
}

@Composable
fun EventTypeDialog(
    event: AdminNotificationEventType?,
    groups: List<AdminNotificationGroup>,
    displayItems: List<AdminNotificationDisplayItem>,
    api: AdminNotificationSettingsApi,
    onSaved: () -> Unit,
    onClose: () -> Unit
) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var code by remember(event) { mutableStateOf(event?.code.orEmpty()) }
    var groupCode by remember(event) { mutableStateOf(event?.groupCode.orEmpty()) }
    var displayItemCode by remember(event) { mutableStateOf(event?.displayItemCode.orEmpty()) }
    var nameEn by remember(event) { mutableStateOf(event?.nameEn.orEmpty()) }
    var nameAr by remember(event) { mutableStateOf(event?.nameAr.orEmpty()) }
    var titleEn by remember(event) { mutableStateOf(event?.titleTemplateEn.orEmpty()) }
    var titleAr by remember(event) { mutableStateOf(event?.titleTemplateAr.orEmpty()) }
    var bodyEn by remember(event) { mutableStateOf(event?.bodyTemplateEn.orEmpty()) }
    var bodyAr by remember(event) { mutableStateOf(event?.bodyTemplateAr.orEmpty()) }
    var hasImage by remember(event) { mutableStateOf(event?.hasImage ?: false) }
    var enabled by remember(event) { mutableStateOf(event?.enabled ?: false) }
    var ttlSeconds by remember(event) { mutableStateOf(event?.ttlSeconds ?: 900) }
    var sampleValues by remember(event) { mutableStateOf(DEFAULT_SAMPLE_VALUES) }
    var preview by remember(event) { mutableStateOf<AdminTemplatePreviewResponse?>(null) }
    var previewError by remember(event) { mutableStateOf<String?>(null) }

    Modal(title = if (event == null) "Add event type" else "Edit event: ${event.code}", isOpen = true, onClose = onClose, maxWidth = 820.px) {
        if (event == null) TextField("Code the vendor sends (lowercase letters, digits, underscore)", code) { code = it.trim() }

        TwoColumns {
            SelectField("Toggle", groupCode, listOf("" to "— not mapped —") + groups.map { it.code to "${it.nameEn} (${it.code})" }) {
                groupCode = it
                if (displayItems.none { item -> item.code == displayItemCode && item.groupCode == it }) displayItemCode = ""
            }
            SelectField(
                "Display item", displayItemCode,
                listOf("" to "— none —") + displayItems.filter { it.groupCode == groupCode }.map { it.code to it.nameEn }
            ) { displayItemCode = it }
        }
        TwoColumns {
            TextField("Name (English)", nameEn) { nameEn = it }
            TextField("Name (Arabic)", nameAr, rtl = true) { nameAr = it }
        }

        Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginBottom(Spacing.md) } }) {
            Text(
                "Use {name} for a value from the event, or {name|fallback} when it may be missing. " +
                    "Common names: home_name, away_name, team_name, scorer_name, player_name, league_name, " +
                    "minute, home_score, away_score, score, article_title, article_summary. " +
                    "An event missing a value that has no fallback is not sent."
            )
        }
        TwoColumns {
            TextField("Title (English)", titleEn) { titleEn = it }
            TextField("Title (Arabic)", titleAr, rtl = true) { titleAr = it }
        }
        TwoColumns {
            TextField("Body (English)", bodyEn, multiline = true) { bodyEn = it }
            TextField("Body (Arabic)", bodyAr, multiline = true, rtl = true) { bodyAr = it }
        }
        TwoColumns {
            NumberField("Valid for (seconds after the event happened)", ttlSeconds) { ttlSeconds = it }
            Div {
                CheckField("Send the event's image when it has one", hasImage) { hasImage = it }
                CheckField("Enabled (sent to users)", enabled) { enabled = it }
            }
        }

        // Preview
        FormField("Preview with sample values (one name=value per line)") {
            TextArea(value = sampleValues) {
                onInput { sampleValues = it.value }
                style { formInputStyle(colors); width(100.percent); property("min-height", "96px"); fontFamily("monospace") }
            }
        }
        Button({
            onClick {
                scope.launch {
                    previewError = null
                    try {
                        preview = api.previewTemplates(
                            AdminTemplatePreviewRequest(titleAr, titleEn, bodyAr, bodyEn, parseSampleValues(sampleValues))
                        )
                    } catch (e: Exception) {
                        previewError = e.message ?: "Preview failed"
                    }
                }
            }
            style { secondaryButtonStyle(colors); padding(Spacing.sm, Spacing.md) }
        }) { Text("Preview") }
        previewError?.let { message -> Div({ style { errorMessageStyle(colors) } }) { Text(message) } }
        preview?.let { result ->
            Div({ style { cardStyle(colors); marginTop(Spacing.md) } }) {
                PreviewLine(result.titleEn, result.bodyEn, rtl = false)
                PreviewLine(result.titleAr, result.bodyAr, rtl = true)
                if (result.missing.isNotEmpty()) {
                    Div({ style { color(Color(colors.danger)); fontSize(Typography.sm); marginTop(Spacing.sm) } }) {
                        Text("No value and no fallback for: ${result.missing.joinToString()}. An event like this would be skipped.")
                    }
                }
            }
        }

        SaveBar(onClose) {
            api.saveEventType(
                AdminNotificationEventType(
                    code = code,
                    groupCode = groupCode.ifBlank { null },
                    displayItemCode = displayItemCode.ifBlank { null },
                    nameAr = nameAr, nameEn = nameEn,
                    titleTemplateAr = titleAr, titleTemplateEn = titleEn,
                    bodyTemplateAr = bodyAr, bodyTemplateEn = bodyEn,
                    hasImage = hasImage, enabled = enabled, ttlSeconds = ttlSeconds
                )
            )
            onSaved()
        }
    }
}

private const val DEFAULT_SAMPLE_VALUES = """home_name=Al Ahly
away_name=Zamalek
team_name=Al Ahly
scorer_name=Emam Ashour
player_name=Emam Ashour
league_name=Egyptian League
minute=54
home_score=1
away_score=0"""

private fun parseSampleValues(text: String): Map<String, String> =
    text.lines().mapNotNull { line ->
        val index = line.indexOf('=')
        if (index <= 0) null else line.substring(0, index).trim() to line.substring(index + 1).trim()
    }.toMap()

@Composable
private fun PreviewLine(title: String?, body: String?, rtl: Boolean) {
    val colors = AppTheme.colors
    if (title.isNullOrBlank() && body.isNullOrBlank()) return
    Div({ style { marginBottom(Spacing.sm); if (rtl) property("direction", "rtl") } }) {
        Div({ style { fontWeight("600"); color(Color(colors.text)) } }) { Text(title.orEmpty()) }
        Div({ style { color(Color(colors.textMuted)) } }) { Text(body.orEmpty()) }
    }
}

@Composable
private fun TwoColumns(content: @Composable () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "1fr 1fr")
            property("column-gap", "16px")
        }
    }) { content() }
}

@Composable
private fun TextField(label: String, value: String, multiline: Boolean = false, rtl: Boolean = false, onChange: (String) -> Unit) {
    val colors = AppTheme.colors
    FormField(label) {
        if (multiline) {
            TextArea(value = value) {
                onInput { onChange(it.value) }
                style {
                    formInputStyle(colors); width(100.percent); property("min-height", "64px")
                    if (rtl) property("direction", "rtl")
                }
            }
        } else {
            Input(InputType.Text) {
                value(value)
                onInput { onChange(it.value) }
                style { formInputStyle(colors); width(100.percent); if (rtl) property("direction", "rtl") }
            }
        }
    }
}

@Composable
private fun NumberField(label: String, value: Int, onChange: (Int) -> Unit) {
    val colors = AppTheme.colors
    FormField(label) {
        Input(InputType.Number) {
            value(value)
            onInput { event -> event.value?.toInt()?.let(onChange) }
            style { formInputStyle(colors); width(160.px) }
        }
    }
}

@Composable
private fun CheckField(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    val colors = AppTheme.colors
    Label(attrs = {
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(Spacing.sm)
            marginBottom(Spacing.md); color(Color(colors.text)); cursor("pointer")
        }
    }) {
        Input(InputType.Checkbox) {
            checked(checked)
            onChange { onChange(it.value) }
        }
        Text(label)
    }
}

@Composable
private fun SelectField(label: String, value: String, options: List<Pair<String, String>>, onChange: (String) -> Unit) {
    val colors = AppTheme.colors
    FormField(label) {
        Select({
            onChange { onChange(it.value ?: "") }
            style { formInputStyle(colors); width(100.percent) }
        }) {
            options.forEach { (key, text) ->
                Option(key, { if (key == value) attr("selected", "") }) { Text(text) }
            }
        }
    }
}

/** Cancel and Save, with the server's validation message shown on failure. */
@Composable
private fun SaveBar(onClose: () -> Unit, save: suspend () -> Unit) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    error?.let { message -> Div({ style { errorMessageStyle(colors) } }) { Text(message) } }
    Div({
        style { display(DisplayStyle.Flex); justifyContent(JustifyContent.End); gap(Spacing.md); marginTop(Spacing.xl) }
    }) {
        Button({
            onClick { onClose() }
            style { secondaryButtonStyle(colors) }
        }) { Text("Cancel") }
        Button({
            if (saving) disabled()
            onClick {
                scope.launch {
                    saving = true
                    error = null
                    try {
                        save()
                        onClose()
                    } catch (e: Exception) {
                        error = e.message ?: "Save failed"
                    } finally {
                        saving = false
                    }
                }
            }
            style { primaryButtonStyle(colors, disabled = saving) }
        }) { Text(if (saving) "Saving…" else "Save") }
    }
}
